import "../style/global.css";

import { Box, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, Skeleton, Stack, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ChatApi, IChat, IChatLine, LanguageLevelEnum, LanguageLevelLabels, LanguageLevelShortLabels } from "../api/ChatApi";
import DropdownArrow from "../asset/dropdown_arrow.png";
import SidebarCollapseIcon from "../asset/icon-sidebar-collapse.png";
import SidebarExpandIcon from "../asset/icon-sidebar-expand.png";
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import ChatListContext from "../context/ChatListContext";
import { pageWhiteCardSx } from "../style/pageWhiteCardSx";
import { SentenceScoreDialogProvider } from "../context/SentenceScoreDialogContext";
import ChatBubble from "./ChatBubble";
import SendButtonTextField from "./SendButtonTextField";

export default function OfficeHourInterface({ isMenuCollapsed = false, onToggleMenuCollapse = () => { }, isMobile = false }) {
    const { setChatList, isLoading: isChatListLoading, setIsLoading: setIsChatListLoading, showSkeleton, selectedChat, setSelectedChat } = React.useContext(ChatListContext);
    const { setErrorMessage } = React.useContext(AppBarContext);

    const history = useHistory();
    const location = useLocation();

    const [sentence, setSentence] = React.useState<string | null>(null);

    const [languageLevel, setLanguageLevel] = React.useState(LanguageLevelEnum.Hsk1);
    const [chatLineList, setChatLineList] = React.useState<IChatLine[]>([]);
    const chatBubbleContainerRef = React.useRef<HTMLDivElement>(null);
    const chatScrollRef = React.useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);

    const firstLine = "Welcome to office hours. What is your question?";

    React.useEffect(() => {
        const chatLine: IChatLine = {
            id: 0,
            chat_id: 0,
            role: "assistant",
            line: firstLine,
            pinyin: [],
        };
        setChatLineList([chatLine]);
    }, []);

    React.useEffect(() => {
        if (selectedChat.id > 0) {
            setLanguageLevel(selectedChat.language_level);
            ChatApi.getChatLineByChat(selectedChat.id)
                .then((response) => {
                    setChatLineList(response.data);
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        } else {
            const chatLine: IChatLine = {
                id: 0,
                chat_id: 0,
                role: "assistant",
                line: firstLine,
                pinyin: [],
            };
            setChatLineList([chatLine]);
        }
    }, [selectedChat, history, location, setErrorMessage]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sentence = event.target.value;
        setSentence(sentence);
    };

    /* Refreshes chat list on side bar */
    const refreshChatList = () => {
        setIsChatListLoading(true);
        ChatApi.getAllOfficeHourChat()
            .then((response) => {
                if (response.status < 300) {
                    const sortedList = response.data.sort((a, b) => b.id - a.id); // Sort newest first
                    setChatList(sortedList);
                } else {
                    handleErrorMessage(response, setErrorMessage, history, location);
                }
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsChatListLoading(false);
            });
    };

    const submitChatResponse = () => {
        if (sentence?.trim()) {
            setIsLoading(true);
            const resolveChatIdForSubmit = (): number => {
                if (selectedChat.id > 0) {
                    return selectedChat.id;
                }
                if (selectedChat.id === -1 && chatLineList.length > 0) {
                    const first = chatLineList[0];
                    const stalePriorTranscript =
                        chatLineList.length > 1 || (chatLineList.length === 1 && first != null && first.chat_id > 0);
                    return stalePriorTranscript ? 0 : first.chat_id;
                }
                if (chatLineList.length > 0) {
                    return chatLineList[0].chat_id;
                }
                return 0;
            };
            const chat_id = resolveChatIdForSubmit();
            const chatLine: IChatLine = {
                id: 0,
                chat_id: chat_id,
                role: "user",
                line: sentence,
                pinyin: [],
            };
            if (chat_id == 0) {
                // start new chat
                ChatApi.startNewOfficeHourChatLine(chatLine, languageLevel)
                    .then((response) => {
                        setChatLineList(response.data);
                        const lines = response.data;
                        const cid = lines.length > 0 ? lines[0].chat_id : 0;
                        if (cid > 0) {
                            const firstUser = lines.find((l) => l.role === "user");
                            const next: IChat = {
                                id: cid,
                                language_level: languageLevel,
                                first_line: firstUser?.line ?? "",
                                latest_chat_date: new Date().toISOString(),
                            };
                            setSelectedChat(next);
                        }
                        refreshChatList(); // refresh the side bar chat list when new chat is added
                    })
                    .catch((err) => {
                        const newChatLine = {
                            id: -1, // Assuming you're generating an ID this way for simplicity
                            chat_id: -1, // Example chat_id, replace with actual data
                            role: "assistant", // Example role, replace with actual data
                            line: "This is a new chat line.", // Example line, replace with actual data
                            pinyin: [],
                        };

                        if (err.response.status == 490) {
                            newChatLine.line = "Please validate your email before chatting. You can validate your email in your profile.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else if (err.response.status == 491) {
                            newChatLine.line = "You can only chat 30 sentences each day.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else if (err.response.status == 492) {
                            newChatLine.line = "This user is read only.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                // Call the update function
                ChatApi.addOfficeHourChatLine(chatLine, languageLevel)
                    .then((response) => {
                        setChatLineList(response.data);
                        refreshChatList();
                    })
                    .catch((err) => {
                        const newChatLine = {
                            id: -1,
                            chat_id: -1,
                            role: "assistant",
                            line: "This is a new chat line.",
                            pinyin: [],
                        };

                        if (err.response && err.response.status === 401) {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        } else if (err.response.status == 490) {
                            newChatLine.line = "Please validate your email before chatting. You can validate your email in your profile.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else if (err.response.status == 491) {
                            newChatLine.line = "You can only chat 30 sentences each day.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else if (err.response.status == 492) {
                            newChatLine.line = "This user is read only.";
                            setChatLineList((prevChatLineList) => [...prevChatLineList, newChatLine]);
                        } else {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        }
    };

    const handleScroll = React.useCallback(() => {
        const el = chatScrollRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const atBottom = scrollHeight - scrollTop - clientHeight < 60; // threshold
        setShowScrollToBottom(!atBottom);
    }, []);

    const scrollToBottom = () => {
        try {
            const unlock = (window as any).__autoScrollLock_globalUnlock;
            if (typeof unlock === 'function') unlock();
        } catch { /* ignore */ }
        if (chatBubbleContainerRef.current) {
            chatBubbleContainerRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleButtonClick = () => {
        if (sentence?.trim()) {
            submitChatResponse();
            setSentence("");
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleButtonClick();
        }
    };

    return (
        <Container
            sx={{
                backgroundColor: "#F2F7FF",
                maxWidth: "none",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                "&.MuiContainer-root": {
                    paddingRight: 2,
                    maxWidth: "none",
                },
            }}>
            <Box
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2, pr: isMobile ? 2 : 4
                }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                        onClick={onToggleMenuCollapse}
                        sx={{
                            minWidth: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                        }}>
                        <img src={isMenuCollapsed ? SidebarExpandIcon : SidebarCollapseIcon} style={{ width: "24px", height: "24px" }} alt={isMenuCollapsed ? "Expand Menu" : "Collapse Menu"} />
                    </Button>
                    <Typography variant="h2" sx={{ color: "#002A6E", marginLeft: "24px" }}>
                        AI Office Hours
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden",
                ...pageWhiteCardSx,
            }}>
                <Stack
                    aria-label="chat messages"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: "100%",
                        overflowY: "auto",
                    }}
                    ref={chatScrollRef}
                    onScroll={handleScroll}
                    position="relative"
                >
                    {(isChatListLoading && showSkeleton) ? (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="flex-start"
                            spacing={2}
                            sx={{ width: '100%', p: 2 }}
                        >
                            {/* Circle Skeleton */}
                            <Skeleton
                                variant="circular" // 👈 this makes it a perfect circle
                                width={60}          // 👈 width and height should match
                                height={60}
                                sx={{
                                    borderRadius: '50%', // technically not needed, 'circular' sets this
                                }}
                            />

                            {/* Wide Rectangle Skeleton */}
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    height: 40,
                                    width: { xs: 275, sm: 300 },
                                    borderRadius: 2,
                                    ml: 2, // margin-left between circle and rectangle
                                }}
                            />
                        </Stack>
                    ) : (
                        <SentenceScoreDialogProvider>
                            {chatLineList.map((chatLine, index) => (
                                <ChatBubble key={chatLine.id} isTeacher={chatLine.role != "user"} message={chatLine.line} messageAudio={null} showAvatar={true} chatBubbleContainerRef={chatBubbleContainerRef} isAnimated={index == chatLineList.length - 1} />
                            ))}
                            <div ref={chatBubbleContainerRef} />
                        </SentenceScoreDialogProvider>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="center" sx={{ pb: 1, position: "relative", flexShrink: 0 }}>
                    <SendButtonTextField value={sentence || ""} onChange={handleInputChange} onSubmit={handleButtonClick} onKeyDown={handleKeyDown} isLoading={isLoading} sx={{ width: "80%", flexGrow: 1, marginRight: 1, marginLeft: 1 }} autoFocus />
                    <FormControl sx={{ marginRight: 1 }}>
                        <InputLabel id="language-level-label">Level</InputLabel>
                        <Select labelId="language-level-label" id="language-level-select" value={languageLevel} label="Level" onChange={(event) => setLanguageLevel(event.target.value as LanguageLevelEnum)}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#ccc',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                    boxShadow: '0 0 0 2px #0062ff',
                                },
                            }}
                            renderValue={(selected) => `${LanguageLevelShortLabels[selected]}`}>
                            {Object.values(LanguageLevelEnum).map((level) => (
                                <MenuItem key={level} value={level}>
                                    {LanguageLevelLabels[level]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {showScrollToBottom && (
                        <IconButton
                            onClick={scrollToBottom}
                            size="small"
                            sx={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                top: -48,
                                backgroundColor: "#ffffffff",
                                width: 40,
                                height: 40,
                                boxShadow: 3,
                                '&:hover': { backgroundColor: '#abcdffff' },
                                border: '2px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-label="Scroll to latest message"
                        >
                            <img src={DropdownArrow} alt="Go to bottom" style={{ width: 20, height: 20, display: 'block' }} />
                        </IconButton>
                    )}
                </Stack>
            </Box>
        </Container>
    );
}
